/* eslint-disable no-nested-ternary */
import React, { ChangeEvent, FC, InputHTMLAttributes, ReactNode, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { nanoid } from 'nanoid'
import Label from '@app/components/Label'
import heic2any from 'heic2any'
import { FormattedMessage, useIntl } from 'react-intl'
import S from './UploadField.styles'
import FieldError from '../FieldError'

interface UploadFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  /**
   * An optional hint to display between the label and the input
   */
  hint?: ReactNode
  /** Optional error component to render */
  error?: string
  // If user uploads heic file, this will be the converted file.
  // If its a jpg/png it will be returned as is
  onOutputFile?: (file: File) => void
}

const UploadField: FC<UploadFieldProps> = ({
  label,
  hint,
  name = nanoid(),
  placeholder = 'No file chosen',
  accept,
  error,
  onChange,
  onBlur,
  onOutputFile, // Destructure the new prop here
}) => {
  const fileInput = useRef<HTMLInputElement | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const { messages } = useIntl()

  const timeout = (ms: number) => {
    // eslint-disable-next-line no-promise-executor-return
    return new Promise((resolve, reject) => setTimeout(reject, ms, 'Timeout reached'))
  }

  const createPreviewUrl = async (f: File) => {
    try {
      let workingFile = f

      if (f.type === 'image/heic' || f.type === 'image/heif') {
        setLoadingPreview(true)
        const blob = await Promise.race([
          heic2any({ blob: new Blob([f]), toType: 'image/jpeg', quality: 1 }),
          timeout(8000),
        ])
        workingFile = new File([blob as BlobPart], f.name, { type: 'image/jpeg' })
        setLoadingPreview(false)
      }

      if (onOutputFile) {
        onOutputFile(workingFile) // Pass the potentially converted file to the parent
      }

      return URL.createObjectURL(workingFile)
    } catch (err) {
      setLoadingPreview(false)
      return null
    }
  }

  const handleFile = async (ev: ChangeEvent<HTMLInputElement>) => {
    onBlur?.({ target: { name } } as any)
    const selectedFile = ev.currentTarget.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
      onChange?.(ev)
      // Create an object URL for the file
      const url = await createPreviewUrl(selectedFile)
      setPreviewUrl(url)
    }
  }
  const handleClick = () => {
    fileInput.current?.click()
  }

  return (
    <S.UploadField>
      {label && <Label htmlFor={name}>{label}</Label>}
      {hint && <S.Hint>{hint}</S.Hint>}
      <div>
        <input
          type="file"
          onChange={handleFile}
          style={{ display: 'none' }}
          ref={fileInput}
          id={name}
          name={name}
          aria-describedby={`${name}-Desc`}
          accept={accept}
          disabled={loadingPreview}
        />
        <p id={`${name}-Desc`} style={{ display: 'none' }}>
          <FormattedMessage id="static.form.button_select_file" />
        </p>
        <S.Button type="button" aria-label={messages['static.form.button_select_file'] as string} onClick={handleClick}>
          {previewUrl ? (
            <S.Preview>
              {file?.type.includes('image') ? (
                <img src={previewUrl} alt="File preview" />
              ) : (
                <Icon icon="bxs:file-pdf" fontSize={40} />
              )}
            </S.Preview>
          ) : loadingPreview ? (
            <S.Icon>
              <S.Spinner>
                <Icon icon="mdi:loading" fontSize={20} />
              </S.Spinner>
            </S.Icon>
          ) : file ? (
            <S.Preview>
              <Icon icon="bxs:file-image" fontSize={40} />
            </S.Preview>
          ) : (
            <S.Icon>
              <Icon icon="icon-park:upload" fontSize={20} />
            </S.Icon>
          )}
          <S.Message>{file ? file.name : placeholder}</S.Message>
        </S.Button>
      </div>
      {!loadingPreview && error && <FieldError show={!!error} message={error} />}
    </S.UploadField>
  )
}

export default UploadField
